import React, { useState, useMemo } from 'react';
import { Modal, Form, Table, notification, Typography } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  QUERY_FEE_LEDGER_RECORDS_TO_VOID,
  MUTATION_VOID_FEE_LEDGER_RECORDS,
} from '../../queries/FeeLedgerQueries';
import { CURRENCY_FORMAT } from '../../constants/numberformats';
import numeral from 'numeral';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import routes from '../../constants/routes';

interface VoidFeeLedgerModalProps {
  isVisible: boolean;
  onClose: () => void;
  assignmentId: string;
  onSuccess?: () => void;
}

const VoidFeeLedgerModal: React.FC<VoidFeeLedgerModalProps> = ({
  isVisible,
  onClose,
  assignmentId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [api, contextHolder] = notification.useNotification();

  const { data: voidableRecords } = useQuery(QUERY_FEE_LEDGER_RECORDS_TO_VOID, {
    variables: { assignment_id: assignmentId },
    skip: !assignmentId || !isVisible,
  });
  const [voidFeeLedgerRecords] = useMutation(MUTATION_VOID_FEE_LEDGER_RECORDS);

  const selectedSum = useMemo(() => {
    if (!voidableRecords?.getFeeLedgerRecordsToVoid) return 0;

    return voidableRecords.getFeeLedgerRecordsToVoid
      .filter((record: any) => selectedRecords.includes(record.record_id))
      .reduce((sum: number, record: any) => sum + record.amount, 0);
  }, [selectedRecords, voidableRecords]);

  const columns = [
    {
      title: 'Container',
      dataIndex: ['assignment', 'container', 'unique_name'],
      key: 'container',
      width: 150,
      render: (name: string, record: any) => (
        <Link
          to={
            routes.CONTAINERS + '/' + record.assignment?.container?.container_id
          }
        >
          {name}
        </Link>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 80,
      render: (amount: number) => numeral(amount).format(CURRENCY_FORMAT),
    },
    {
      title: 'Assessed On',
      dataIndex: 'assessed_on',
      key: 'assessed_on',
      width: 160,
      render: (date: string) => dayjs(date).format('LLL'),
    },
    {
      title: 'Processed On',
      dataIndex: 'processed_on',
      key: 'processed_on',
      width: 160,
      render: (date: string) =>
        date ? dayjs(date).format('LLL') : '- not set -',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 100,
      render: (note: string) => note || '- not set -',
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRecords,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRecords(selectedRowKeys as string[]);
    },
  };

  const handleSubmit = async () => {
    try {
      if (selectedRecords.length === 0) {
        api.error({ message: 'Please select at least one record to void' });
        return;
      }

      await voidFeeLedgerRecords({
        variables: {
          record_ids: selectedRecords,
        },
      });

      api.success({ message: 'Records voided successfully' });
      handleClose();
      onSuccess?.();
    } catch (error) {
      api.error({ message: 'Failed to void records' });
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedRecords([]);
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Void Fees"
        open={isVisible}
        width={1000}
        onOk={handleSubmit}
        onCancel={handleClose}
        okText={
          selectedRecords.length > 0
            ? `VOID ${numeral(selectedSum).format(CURRENCY_FORMAT)} IN FEES`
            : 'VOID FEES'
        }
      >
        <Form form={form} layout="vertical">
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={voidableRecords?.getFeeLedgerRecordsToVoid || []}
            rowKey="record_id"
            pagination={false}
            scroll={{ y: 300 }}
          />
          {selectedRecords.length > 0 && (
            <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
              Total amount to void:{' '}
              {numeral(selectedSum).format(CURRENCY_FORMAT)}
            </Typography.Text>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default VoidFeeLedgerModal;
